import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown } from "lucide-react";

const EmailInput = ({ emails = [], onChange, label, placeholder }) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      addEmail();
    } else if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
      removeEmail(emails.length - 1);
    }
  };

  const addEmail = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && validateEmail(trimmedValue)) {
      if (!emails.includes(trimmedValue)) {
        onChange([...emails, trimmedValue]);
      }
      setInputValue("");
    }
  };

  const removeEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    onChange(newEmails);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emailList = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter((email) => email && validateEmail(email));

    const uniqueEmails = [...new Set([...emails, ...emailList])];
    onChange(uniqueEmails);
    setInputValue("");
  };

  return (
    <div className="w-full">
      {label && <div className="text-xs text-gray-600 mb-1 font-medium">{label}</div>}
      <div
        className={`min-h-[42px] border rounded-md p-2 flex flex-wrap gap-1 items-center bg-white ${
          isFocused ? "" : "border-gray-300"
        } transition-colors`}
        onClick={(e) => {
          // Stop event propagation to prevent parent handlers from interfering
          e.stopPropagation();
          
          // Only focus if clicking on empty space in container, not on input field itself
          // The input field will handle its own focus naturally
          if (e.target !== inputRef.current && !inputRef.current?.contains(e.target)) {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }
        }}
      >
        {emails.map((email, index) => (
          <div
            key={index}
            className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-sm font-medium"
          >
            <span className="text-xs">{email}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeEmail(index);
              }}
              className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
            >
              <X size={14} className="text-blue-700" />
            </button>
          </div>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            if (inputValue.trim()) {
              addEmail();
            }
          }}
          placeholder={emails.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
        />
      </div>
    </div>
  );
};

const EmailSection = ({ email, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  useEffect(() => {
    // Show Cc/Bcc if they have existing values
    if (email?.cc && email.cc.length > 0) {
      setShowCc(true);
    }
    if (email?.bcc && email.bcc.length > 0) {
      setShowBcc(true);
    }
  }, [email]);

  const handleEmailChange = (field, value) => {
    onChange({
      ...email,
      [field]: value,
    });
  };

  // Show Cc/Bcc if they have values or if explicitly shown
  const shouldShowCc = showCc || (email.cc && email.cc.length > 0);
  const shouldShowBcc = showBcc || (email.bcc && email.bcc.length > 0);

  return (
    <div className="bg-white shadow-md rounded-xl border border-gray-200 w-full transition-all duration-300 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between gap-2 p-5 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 rounded-lg">
            <svg
              className="w-5 h-5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-gray-800">Email Recipients</h3>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-600 transition-transform duration-300 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>
      
      <div
        className={`space-y-4 px-5  transition-all duration-100 ease-in-out ${
          isExpanded ? "max-h-[1000px] opacity-100 pb-5" : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="text-sm text-gray-700 font-semibold pt-2.5 min-w-[45px]">To</div>
          <div className="flex-1">
            <EmailInput
              emails={email.to || []}
              onChange={(emails) => handleEmailChange("to", emails)}
              label=""
              placeholder="Recipients"
            />
          </div>
        </div>

        {/* {shouldShowCc && ( */}
          <div className="flex items-start gap-3">
            <div className="text-sm text-gray-700 font-semibold pt-2.5 min-w-[45px]">Cc</div>
            <div className="flex-1">
              <EmailInput
                emails={email.cc || []}
                onChange={(emails) => handleEmailChange("cc", emails)}
                label=""
                placeholder="Recipients"
              />
            </div>
          </div>
        {/* )} */}

        {/* {shouldShowBcc && ( */}
          <div className="flex items-start gap-3">
            <div className="text-sm text-gray-700 font-semibold pt-2.5 min-w-[45px]">Bcc</div>
            <div className="flex-1">
              <EmailInput
                emails={email.bcc || []}
                onChange={(emails) => handleEmailChange("bcc", emails)}
                label=""
                placeholder="Recipients"
              />
            </div>
          </div>
        {/* )} */}

        {/* <div className="flex items-center gap-4 pl-[52px]">
          {!shouldShowCc && (
            <button
              type="button"
              onClick={() => {
                setShowCc(true);
                if (!email.cc) {
                  handleEmailChange("cc", []);
                }
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Cc
            </button>
          )}
          {!shouldShowBcc && (
            <button
              type="button"
              onClick={() => {
                setShowBcc(true);
                if (!email.bcc) {
                  handleEmailChange("bcc", []);
                }
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Bcc
            </button>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default EmailSection;

